import React from 'react';

import { Edit } from '@mui/icons-material';
import type { PopperProps } from '@mui/material';
import { Button, Divider, Paper, Popper, Slide, Stack, Typography, useTheme } from '@mui/material';
import { forEach, get, isEmpty, isNil, map, set, size } from 'lodash';
import { useMatch, useNavigate } from 'react-router-dom';
import { ConfirmLoadingButton } from '../../../../components';
import { createScopedI18n } from '../../../../i18n/i18n';
import { joinPairs } from '../../../../utils/libs';
import snackbar, { GENERIC_ERROR, SAVE_SUCCESS } from '../../../../utils/snackbar';
import { AttendancesPageContext } from '../attendances_page_context';
import useApproveAttendancesMutation from '../hooks/use_approve_attendances_mutation';
import useRejectAttendancesMutation from '../hooks/use_reject_attendances_mutation';

export type BatchToolbarProps = Omit<PopperProps, 'children' | 'open'> & {
  open?: boolean;
  disableActions?: boolean;
};

type BatchToolbarInnerProps = BatchToolbarProps & {
  open: boolean;
  selectedAttendanceIds?: string[];
  setOpenBatchModal: React.Dispatch<React.SetStateAction<boolean>>;
  isEditModalOpen?: boolean;
  selectAttendance?: (attendanceId: string, selected: boolean) => void;
};

const attendancePageI18n = createScopedI18n('pages.attendances');

const BatchToolbar = React.memo(
  ({
    open,
    selectedAttendanceIds,
    setOpenBatchModal,
    isEditModalOpen,
    disableActions,
    selectAttendance,
    ...props
  }: BatchToolbarInnerProps) => {
    const theme = useTheme();

    const navigate = useNavigate();
    const detailPageMatch = useMatch('/attendances/:id');
    const isDetailPageOpen = !isNil(detailPageMatch?.params.id);

    const {
      approveAttendances,
      result: { loading: approveLoading },
    } = useApproveAttendancesMutation();
    const {
      rejectAttendances,
      result: { loading: rejectLoading },
    } = useRejectAttendancesMutation();

    const isActionLoading = approveLoading || rejectLoading;

    const approveHandler = React.useCallback(async () => {
      if (isEmpty(selectedAttendanceIds)) return;

      const result = await approveAttendances(map(selectedAttendanceIds, (id) => ({ id })));

      if (result.success) {
        snackbar.enqueueSuccess(SAVE_SUCCESS);
        forEach(selectedAttendanceIds, (id) => selectAttendance?.(id, false));
        setOpenBatchModal(false);
      } else if (result.errors || result.error) {
        const errorMessage = result.errors ? joinPairs(result.errors) : get(result.error, 'message', 'Unknown error');
        snackbar.enqueueError(GENERIC_ERROR, { message: errorMessage });
      }
    }, [approveAttendances, selectedAttendanceIds, selectAttendance, setOpenBatchModal]);

    const rejectHandler = React.useCallback(async () => {
      if (isEmpty(selectedAttendanceIds)) return;

      const result = await rejectAttendances(map(selectedAttendanceIds, (id) => ({ id })));

      if (result.success) {
        snackbar.enqueueSuccess(SAVE_SUCCESS);
        forEach(selectedAttendanceIds, (id) => selectAttendance?.(id, false));
        setOpenBatchModal(false);
      } else if (result.errors || result.error) {
        const errorMessage = result.errors ? joinPairs(result.errors) : get(result.error, 'message', 'Unknown error');
        snackbar.enqueueError(GENERIC_ERROR, { message: errorMessage });
      }
    }, [rejectAttendances, selectedAttendanceIds, selectAttendance, setOpenBatchModal]);

    const onClickEditButtonHandler = React.useCallback(() => {
      setOpenBatchModal(true);
      // Close any open detail drawer so the batch edit drawer is visible.
      // Keep the batch drawer open; users can still open detail pages afterwards.
      if (isDetailPageOpen) {
        navigate('/attendances', { replace: true });
      }
    }, [isDetailPageOpen, setOpenBatchModal]);

    return (
      <Popper
        open={open}
        disablePortal
        transition
        {...props}
        style={{
          position: 'fixed',
          top: 'unset',
          bottom: 80,
          // Center horizontally when batch modal is closed, align to left when batch modal is open
          left: isEditModalOpen ? '32px' : '50%',
          // Shift left by 50% of itself width to center it when batch modal is closed, no shift when batch modal is open
          transform: isEditModalOpen ? 'translateX(0)' : 'translateX(-50%)',
          transition: 'all 0.3s ease-in-out',
          maxWidth: 'calc(100% - 32px)',
          zIndex: theme.zIndex.drawer - 1,
          ...props.style,
        }}
      >
        {({ TransitionProps }) => (
          <Slide {...TransitionProps} direction="up" timeout={100}>
            <Paper elevation={12} sx={{ borderRadius: 4 }}>
              <Stack gap={1} flexDirection="row" paddingX={2} paddingY={1.5} alignItems="center">
                {isNil(selectedAttendanceIds) ? null : (
                  <>
                    <Stack
                      width={24}
                      height={24}
                      justifyContent="center"
                      alignItems="center"
                      borderRadius="50%"
                      bgcolor={theme.palette.divider}
                    >
                      <Typography variant="caption">{size(selectedAttendanceIds)}</Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                  </>
                )}

                <Stack gap={1} flexDirection="row" alignItems="center">
                  <ConfirmLoadingButton
                    variant="outlined"
                    color="primary"
                    size="small"
                    loading={approveLoading}
                    disabled={disableActions || isActionLoading}
                    onClick={approveHandler}
                  >
                    {attendancePageI18n('approve')}
                  </ConfirmLoadingButton>
                  <ConfirmLoadingButton
                    variant="outlined"
                    color="error"
                    size="small"
                    loading={rejectLoading}
                    disabled={disableActions || isActionLoading}
                    onClick={rejectHandler}
                  >
                    {attendancePageI18n('reject')}
                  </ConfirmLoadingButton>
                </Stack>

                <Divider orientation="vertical" flexItem />

                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Edit />}
                  disableElevation
                  disabled={isActionLoading}
                  onClick={onClickEditButtonHandler}
                >
                  {attendancePageI18n('edit')}
                </Button>
              </Stack>
            </Paper>
          </Slide>
        )}
      </Popper>
    );
  },
);

const BatchToolbarContainer = ({ open, disableActions, ...rest }: BatchToolbarProps) => {
  const { selectedAttendancesIds, openBatchModal, setOpenBatchModal, hasUnsavedChanges, selectAttendance } =
    React.useContext(AttendancesPageContext);

  const isVisible = React.useMemo(() => !isEmpty(selectedAttendancesIds), [selectedAttendancesIds]);

  const selectedAttendancesIdsArray = React.useMemo(
    () => [...(selectedAttendancesIds ?? [])],
    [selectedAttendancesIds],
  );

  const match = useMatch('/attendances/:id');
  const isDetailPageOpen = !isNil(match?.params.id);

  return (
    <BatchToolbar
      open={open ?? isVisible}
      selectedAttendanceIds={selectedAttendancesIdsArray}
      setOpenBatchModal={setOpenBatchModal}
      isEditModalOpen={openBatchModal || isDetailPageOpen}
      disableActions={disableActions ?? hasUnsavedChanges}
      selectAttendance={selectAttendance}
      {...rest}
    />
  );
};

export default BatchToolbarContainer;
